import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Zoom,
  Fade,
} from '@mui/material';
import {
  Close,
  Satellite,
  Map as MapIcon,
  Water as WaterIcon,
  Agriculture as FarmIcon,
  Terrain,
  LocationOn,
  Info,
} from '@mui/icons-material';

const PondImagePopup = ({ open, onClose, pond }) => {
  if (!pond) return null;

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.nextSibling.style.display = 'block';
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '95vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Satellite sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {pond.pond_name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Pond & Geospatial Information
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={0} sx={{ height: '100%' }}>
          {/* Left Side - Pond Image */}
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              position: 'relative', 
              flex: 1,
              minHeight: { xs: '300px', sm: '400px', md: '500px' },
              maxHeight: { xs: '400px', sm: '500px', md: '60vh' },
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {pond.geospatial_image_url ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    src={pond.geospatial_image_url}
                    alt={`${pond.pond_name} - Satellite View`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={handleImageError}
                    onLoad={(e) => {
                      // Ensure image fits properly
                      const img = e.target;
                      const container = img.parentElement;
                      const containerRect = container.getBoundingClientRect();
                      const imgRect = img.getBoundingClientRect();
                      
                      // If image is smaller than container, center it
                      if (imgRect.width < containerRect.width || imgRect.height < containerRect.height) {
                        img.style.objectFit = 'contain';
                      }
                    }}
                  />
                  <Box
                    sx={{
                      display: 'none',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    <Satellite sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6">Image not available</Typography>
                    <Typography variant="body2">Satellite view could not be loaded</Typography>
                  </Box>
                  
                  {/* Image Overlay Info */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      p: 2,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {pond.pond_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {pond.pond_area && (
                        <Chip
                          label={`${parseFloat(pond.pond_area).toFixed(2)} ha`}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      )}
                      {pond.species && (
                        <Chip
                          label={pond.species}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Zoom Button */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<MapIcon />}
                      onClick={() => openInNewTab(pond.geospatial_image_url)}
                      sx={{
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.8)',
                        },
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      Open Full Size
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary',
                    p: 4
                  }}
                >
                  <Satellite sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    No Satellite Image
                  </Typography>
                  <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 400 }}>
                    This pond doesn't have a geospatial image assigned yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Side - Pond Details */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              p: { xs: 2, sm: 3 }, 
              flex: 1,
              overflow: 'auto',
              maxHeight: { xs: '400px', sm: '500px', md: '60vh' }
            }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                Pond Information
              </Typography>

              {/* Basic Information */}
              <Card sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    Basic Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WaterIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2">
                        <strong>Name:</strong> {pond.pond_name}
                      </Typography>
                    </Box>
                    {pond.pond_area && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Terrain sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2">
                          <strong>Area:</strong> {parseFloat(pond.pond_area).toFixed(2)} hectares
                        </Typography>
                      </Box>
                    )}
                    {pond.species && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FarmIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2">
                          <strong>Species:</strong> {pond.species}
                        </Typography>
                      </Box>
                    )}
                    {pond.stocking_density && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2">
                          <strong>Stocking Density:</strong> {parseFloat(pond.stocking_density).toFixed(2)}/m²
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Location Information */}
              {(pond.latitude || pond.longitude) && (
                <Card sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Location
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {pond.latitude && pond.longitude && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Coordinates:</strong> {parseFloat(pond.latitude).toFixed(2)}, {parseFloat(pond.longitude).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {pond.latitude && pond.longitude && (
                        <Button
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={() => openInNewTab(`https://www.google.com/maps?q=${pond.latitude},${pond.longitude}`)}
                          sx={{ alignSelf: 'flex-start', mt: 1 }}
                        >
                          View on Google Maps
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Additional Information */}
              {(pond.water_source_type || pond.soil_type) && (
                <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Environmental Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {pond.water_source_type && (
                        <Typography variant="body2">
                          <strong>Water Source:</strong> {pond.water_source_type}
                        </Typography>
                      )}
                      {pond.soil_type && (
                        <Typography variant="body2">
                          <strong>Soil Type:</strong> {pond.soil_type}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, background: '#f8fafc' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Close
        </Button>
        {pond.geospatial_image_url && (
          <Button
            onClick={() => openInNewTab(pond.geospatial_image_url)}
            variant="contained"
            startIcon={<Satellite />}
          >
            Open Full Image
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PondImagePopup;
